import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import PlayIcon from '@8x8/oxygen-icon/icons/PlayCircleIcon'

import { retrievePresignedUrl, setPlayingRecording } from '~/actions/recents'
import { Flex } from '~/components/common/containers'
import { getIsMobileSummaryPage } from '~/selectors/app'
import { getPlayingRecording } from '~/selectors/recents'
import Button from '~/components/common/button'
import { translate } from '~/core/translate'
import { FUNCTIONS } from '~/core/constants'

const PlayFullRecordingButton = styled(Button)`
  align-items: center;
  display: flex;
  margin: 16px calc(50% - 100px) 0;
  padding: 8px 16px 8px;
  position: absolute;

  svg {
    margin-right: 8px;
  }
`

const Container = styled(Flex).attrs({
  fullHeight: true,
  fullWidth: true
})`
  background-color: black;

  video {
    height: 100%;
    object-fit: ${({ isPlaying }) => isPlaying ? 'contain' : 'cover'};
    pointer-events: ${({ isPlaying }) => isPlaying ? 'all' : 'none'};
    width: 100%;
  }
`

const RecordingVideoPlayer = ({
  canPlay = true,
  className,
  onEnd = FUNCTIONS.NOOP,
  recording
}) => {
  const dispatch = useDispatch()
  const videoRef = useRef()
  const video = videoRef.current
  const [videoUrl, setVideoUrl] = useState(null)
  const isMobileSummaryPage = useSelector(getIsMobileSummaryPage)
  const controlsList = isMobileSummaryPage ? 'nodownload' : ''
  const playingRecording = useSelector(getPlayingRecording)
  const isPlaying = canPlay && playingRecording?.url === recording.url
  const displayPlayFullRecording = recording.isHighlight && isPlaying

  const resetTime = () => {
    if (video) {
      // reset to 0 to get the same thumbnail
      video.currentTime = 0
      video.pause()
    }
  }

  const getVideoUrl = async () => {
    if (!recording) {
      return
    }

    const url = await dispatch(retrievePresignedUrl(recording.url))

    if (url) {
      setVideoUrl(url)
    } else {
      dispatch(setPlayingRecording(null))
    }
  }

  const playFullRecording = e => {
    e.stopPropagation?.()

    resetTime()

    dispatch(setPlayingRecording({
      currentTime: recording.capturedAtSecond || 0,
      url: recording.fullUrl
    }))
  }

  useEffect(() => {
    resetTime()
    getVideoUrl()
  }, [recording])

  useEffect(() => {
    if (video) {
      video.onended = onEnd

      if (isPlaying) {
        video.currentTime = playingRecording?.currentTime || 0
        video.play()
      }
    }
  }, [video, isPlaying, videoUrl])

  useEffect(() => {
    return () => {
      dispatch(setPlayingRecording(null))
    }
  }, [])

  return (
    <Container
      className={className}
      isPlaying={isPlaying}
    >
      <video
        controls={isPlaying}
        controlsList={controlsList}
        disablePictureInPicture
        ref={videoRef}
        src={videoUrl}
      />
      {displayPlayFullRecording && (
        <PlayFullRecordingButton onClick={playFullRecording}>
          <PlayIcon size={24} color='white' />
          {translate('recording_watch_full')}
        </PlayFullRecordingButton>
      )}
    </Container>
  )
}

export default RecordingVideoPlayer
